/**
 * Per-column configuration for a data_list (ADR-0010 G1 filters + G2 chooser).
 *
 * One row per column, hosting the per-column settings that would otherwise
 * bloat the inspector sidebar:
 *  - **Filter** (G1, shown when `filterable`): Text (free-text `contains`, the
 *    default), Multiselect (a checkbox list over admin-set `options` — closed-
 *    domain fields), or Off. Multiselect options are authored here and baked
 *    into the AST so the runtime never runs a blind distinct; an optional
 *    "Load from data" pulls the distinct values on demand (explicit).
 *  - **Visible** (G2, shown when `column_chooser`): the column's default-on
 *    state in the runtime column chooser. Off = hidden until the user enables it.
 *  - **Pin** (G3, shown when `pinnable`): freeze the column to the left so it
 *    stays visible while the rest of the table scrolls horizontally.
 *
 * The map stores ONLY non-default columns (filter Text + visible + unpinned).
 */
import { useState } from "react";

import { ColumnsField } from "./ColumnsField";
import { Field, Select } from "./inspectorKit";
import { Switch } from "@/components/ui/switch";

type FilterMode = "none" | "text" | "select" | "range" | "daterange";
type ColCfg = { filter?: FilterMode; options?: string[]; hidden?: boolean; pin?: "left" | "right" };

export interface ColumnFilterConfigEditorProps {
  value: unknown;
  /** Column names of the bound query (from the data_list preview). */
  columns: string[];
  /** Optional: pull a column's distinct values (explicit, admin-triggered). */
  loadValues?: (column: string) => Promise<string[]>;
  /** Show the per-column filter mode picker (G1, `filterable` on). Default on. */
  showFilter?: boolean;
  /** Show the per-column "visible by default" toggle (G2, `column_chooser` on). */
  showVisibility?: boolean;
  /** Show the per-column "pin left" toggle (G3, `pinnable` on). */
  showPin?: boolean;
  onChange: (config: Record<string, ColCfg>) => void;
}

/** A column entry is default (droppable) when its filter is Text/absent AND it
 *  isn't hidden AND isn't pinned — i.e. carries nothing worth persisting. */
function isDefault(cfg: ColCfg | undefined): boolean {
  if (!cfg) return true;
  return (cfg.filter ?? "text") === "text" && !cfg.hidden && !cfg.pin;
}

export function ColumnFilterConfigEditor({
  value,
  columns,
  loadValues,
  showFilter = true,
  showVisibility = false,
  showPin = false,
  onChange,
}: ColumnFilterConfigEditorProps) {
  const cfg = (value && typeof value === "object" ? value : {}) as Record<string, ColCfg>;
  const [loading, setLoading] = useState<string | null>(null);

  const set = (col: string, next: ColCfg) => {
    const m = { ...cfg };
    if (isDefault(next)) delete m[col];
    else m[col] = next;
    onChange(m);
  };

  if (columns.length === 0) {
    return <p className="text-[11px] text-muted-foreground">Bind a query first to configure columns.</p>;
  }

  const MODE_OPTS = [
    { value: "text", label: "Text" },
    { value: "select", label: "Multiselect" },
    { value: "range", label: "Range (min/max)" },
    { value: "daterange", label: "Range (dates)" },
    { value: "none", label: "Off" },
  ];

  return (
    <div className="flex flex-col gap-2">
      {columns.map((c) => {
        const cur = cfg[c] ?? {};
        const mode = cur.filter ?? "text";
        return (
          <div key={c} className="rounded-md border border-border/70 p-2">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-xs font-medium text-foreground" title={c}>
                {c}
              </span>
              <div className="flex shrink-0 items-center gap-3">
                {showPin && (
                  <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    Pin
                    <Select
                      value={cur.pin ?? "none"}
                      options={[
                        { value: "none", label: "Off" },
                        { value: "left", label: "Left" },
                        { value: "right", label: "Right" },
                      ]}
                      onChange={(v) => set(c, { ...cur, pin: v === "none" ? undefined : (v as "left" | "right") })}
                    />
                  </label>
                )}
                {showVisibility && (
                  <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    Visible
                    <Switch
                      checked={!cur.hidden}
                      onCheckedChange={(v) => set(c, { ...cur, hidden: !v })}
                    />
                  </label>
                )}
              </div>
            </div>
            {showFilter && (
              <Field label="Filter">
                <Select
                  value={mode}
                  options={MODE_OPTS}
                  onChange={(v) =>
                    set(c, {
                      ...cur,
                      filter: v as FilterMode,
                      // Drop stale options when leaving Multiselect.
                      options: v === "select" ? cur.options ?? [] : undefined,
                    })
                  }
                />
              </Field>
            )}
            {showFilter && mode === "select" && (
              <div className="mt-1.5 flex flex-col gap-1">
                <ColumnsField
                  value={cur.options ?? []}
                  options={[]}
                  onChange={(opts) => set(c, { ...cur, filter: "select", options: (opts as string[]) ?? [] })}
                />
                {loadValues && (
                  <button
                    type="button"
                    disabled={loading === c}
                    onClick={async () => {
                      setLoading(c);
                      try {
                        const vals = await loadValues(c);
                        set(c, { ...cur, filter: "select", options: vals });
                      } finally {
                        setLoading(null);
                      }
                    }}
                    className="self-start text-[11px] font-medium text-accent hover:underline disabled:opacity-50"
                  >
                    {loading === c ? "Loading…" : "Load values from data"}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
